import { useEffect, useState } from "react";
import IconOnlyButton from "./IconOnlyButton";
import CopyIcon from "../../Icons/CopyIcon";
import { copyToClipboard } from "../../../shared/utils";

/**
 * Button for copying text.
 * @param {object} props
 * @param {string} props.textToCopy The text that will be copied when the button is clicked.
 * @param {"primary" | "secondary" | "danger" | { buttonBackgroundColor: string; buttonTextColor: string; }} props.colorTheme The color theme for the button. Defaults to `primary`.
 * @param {string} props.copySuccessMessage Message that's displayed when the button is clicked and text is copied.
 * @param {string} props.copyFailMessage Message that's displayed when the button is clicked and text isn't copied.
 */
function CopyButton({
    textToCopy,
    colorTheme = "primary",
    copySuccessMessage = "Copied!",
    copyFailMessage = "Copy failed",
}) {
    const [showTooltip, setShowTooltip] = useState(false);

    const [tooltipText, setTooltipText] = useState(copySuccessMessage);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setShowTooltip(false);
        }, 2000);

        return () => clearTimeout(timeout);
    }, [showTooltip]);

    async function copyToClipboardAndSetTooltip() {
        setShowTooltip(true);

        try {
            copyToClipboard(textToCopy);

            if (tooltipText !== copySuccessMessage) {
                setTooltipText(copySuccessMessage);
            }
        } catch (error) {
            setTooltipText(copyFailMessage);
            console.error(error);
        }
    }

    return (
        <IconOnlyButton
            size="xs"
            colorTheme={colorTheme}
            handleOnClick={copyToClipboardAndSetTooltip}
            iconComponent={<CopyIcon />}
            tooltipProps={{
                text: tooltipText,
                show: showTooltip,
            }}
            aria-label="copy"
        />
    );
}

export default CopyButton;
